import clsx from 'clsx';
import { motion } from 'framer-motion';

type IOSSwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
};

export const IOSSwitch = ({
  checked,
  onChange,
  disabled = false,
  size = 'md',
  className = '',
}: IOSSwitchProps) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const sizeConfig = {
    sm: {
      wrapper: 'w-10 h-6',
      thumb: 'w-4 h-4',
      translateDistance: 16, // 40px wrapper - 16px thumb - 8px padding = 16px
    },
    md: {
      wrapper: 'w-12 h-7',
      thumb: 'w-5 h-5',
      translateDistance: 20, // 48px wrapper - 20px thumb - 8px padding = 20px
    },
  };

  const config = sizeConfig[size];

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={clsx(
        'relative inline-flex flex-shrink-0 rounded-full transition-colors duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 focus:ring-offset-zinc-900',
        config.wrapper,
        checked ? 'bg-violet-500' : 'bg-zinc-600',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className,
      )}
      aria-pressed={checked}
    >
      <span className="sr-only">Toggle switch</span>
      <motion.span
        className={clsx(
          'absolute top-1 left-1 pointer-events-none inline-block rounded-full bg-white shadow-md',
          config.thumb,
        )}
        initial={false}
        animate={{
          x: checked ? config.translateDistance : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 700,
          damping: 40,
          mass: 0.8,
        }}
      />
    </button>
  );
};
